import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, X, Clock, UserCheck, UserX, RefreshCw, FileText, Mail, Building, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDownloadRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const url = filter === 'all' 
        ? `${API}/api/download-requests/admin/all`
        : `${API}/api/download-requests/admin/all?status=${filter}`;
      const res = await fetch(url);
      const data = await res.json();
      setRequests(data.requests || []);
    } catch (err) {
      toast.error('Failed to fetch requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const approveRequest = async (id) => {
    try {
      const res = await fetch(`${API}/api/download-requests/admin/approve/${id}`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Request approved');
        fetchRequests();
      }
    } catch (err) {
      toast.error('Failed to approve request');
    }
  };

  const denyRequest = async (id) => {
    try {
      const res = await fetch(`${API}/api/download-requests/admin/deny/${id}`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Request denied');
        fetchRequests();
      }
    } catch (err) {
      toast.error('Failed to deny request');
    }
  };

  const revokeAccess = async (id) => {
    if (!window.confirm('Are you sure you want to revoke this access?')) return;
    try {
      const res = await fetch(`${API}/api/download-requests/admin/revoke/${id}`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Access revoked');
        fetchRequests();
      }
    } catch (err) {
      toast.error('Failed to revoke access');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      approved: 'bg-green-500/20 text-green-400 border-green-500/30',
      denied: 'bg-red-500/20 text-red-400 border-red-500/30',
      revoked: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs border ${styles[status] || styles.pending}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const getDocTypeBadge = (type) => {
    const labels = {
      proposal: 'Proposal',
      budget: 'Budget',
      action_plan: 'Action Plan',
      all: 'All Documents'
    };
    return (
      <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs">
        {labels[type] || type}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-6">
      {/* Header */}
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">Download Access Requests</h1>
            <p className="text-gray-400 text-sm">Manage who can download funding documents</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex gap-2">
            {['all', 'pending', 'approved', 'denied'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === f 
                    ? 'bg-amber-500 text-black' 
                    : 'bg-white/10 hover:bg-white/20'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <button
            onClick={fetchRequests}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors ml-auto"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4">
            <div className="flex items-center gap-2 text-yellow-400 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-sm">Pending</span>
            </div>
            <div className="text-2xl font-bold text-yellow-400">
              {requests.filter(r => r.status === 'pending').length}
            </div>
          </div>
          <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
            <div className="flex items-center gap-2 text-green-400 mb-1">
              <UserCheck className="w-4 h-4" />
              <span className="text-sm">Approved</span>
            </div>
            <div className="text-2xl font-bold text-green-400">
              {requests.filter(r => r.status === 'approved').length}
            </div>
          </div>
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
            <div className="flex items-center gap-2 text-red-400 mb-1">
              <UserX className="w-4 h-4" />
              <span className="text-sm">Denied</span>
            </div>
            <div className="text-2xl font-bold text-red-400">
              {requests.filter(r => r.status === 'denied').length}
            </div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-400 mb-1">
              <FileText className="w-4 h-4" />
              <span className="text-sm">Total</span>
            </div>
            <div className="text-2xl font-bold">
              {requests.length}
            </div>
          </div>
        </div>

        {/* Requests List */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            No requests found
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map(req => (
              <div 
                key={req.id} 
                className={`bg-[#141414] border rounded-xl p-5 ${
                  req.status === 'pending' ? 'border-yellow-500/30' : 'border-white/10'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-lg">{req.name}</h3>
                      {getStatusBadge(req.status)}
                      {getDocTypeBadge(req.document_type)}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-400 mb-3">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${req.email}`} className="hover:text-amber-400">
                          {req.email}
                        </a>
                      </div>
                      {req.organization && (
                        <div className="flex items-center gap-2">
                          <Building className="w-4 h-4" />
                          {req.organization}
                        </div>
                      )}
                    </div>
                    
                    <div className="bg-black/30 rounded-lg p-3 mb-3">
                      <div className="flex items-start gap-2 text-sm">
                        <MessageSquare className="w-4 h-4 text-gray-500 mt-0.5" />
                        <p className="text-gray-300">{req.reason}</p>
                      </div>
                    </div>
                    
                    <div className="text-xs text-gray-500">
                      Requested: {new Date(req.created_at).toLocaleString()}
                      {req.reviewed_at && (
                        <span className="ml-4">
                          Reviewed: {new Date(req.reviewed_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    {req.status === 'pending' && (
                      <>
                        <button
                          onClick={() => approveRequest(req.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-black font-bold rounded-lg transition-colors"
                        >
                          <Check className="w-4 h-4" />
                          Approve
                        </button>
                        <button
                          onClick={() => denyRequest(req.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4" />
                          Deny
                        </button>
                      </>
                    )}
                    {req.status === 'approved' && (
                      <button
                        onClick={() => revokeAccess(req.id)}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-500/20 hover:bg-gray-500/30 text-gray-400 rounded-lg transition-colors"
                      >
                        <UserX className="w-4 h-4" />
                        Revoke
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDownloadRequests;
